import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import { Icon, Modal, StatePanel } from "../../components/ui";
import "./market-signals.scss";

/**
 * Market signals, in a side panel over the Arbitrages page (Alex, 2026-10-13).
 *
 * Each signal is computed nightly (ArbitrageSignalService) and lists every
 * model where it is active, strongest first. A row opens to the references
 * behind it; "Show" filters the grid to that model, "Show all" to every model
 * in the signal. A signal with nothing to report tonight is not shown.
 *
 * Rules are one tap away (ⓘ) rather than printed, to keep the panel quiet.
 */
const SIGNALS = [
  { key: "lifecycle", title: "Route lifecycle", rule: "Tonight's routes against the last 14 nights. Fading means a steady slide toward the 5% floor; the date is when it gets there.", tabs: ["New tonight", "Persistent", "Fading", "Closed"] },
  { key: "short", title: "Unusually short supply", rule: "Buyers (WTB leads) per trade listing over 30 days, against the model's 12-month average. Shown at 2× or more with 5+ leads." },
  { key: "leadlag", title: "Which market moves first", rule: "Weekly trade prices by region. Shown when one region's moves are repeated by another 1–4 weeks later in at least 4 of the last 6 moves." },
  { key: "version", title: "Which version to buy", rule: "Premium for recent full sets (box and papers, last 3 years) where you sell, against where you buy." },
  { key: "speed", title: "Where it sells fastest", rule: "Median days a market listing stays up before it disappears, by region. Needs 15+ listings per region." },
  { key: "missed", title: "Missed deals", rule: "A WTB lead with a budget and a trade quote at or under it within 7 days. Open leads can still be won." },
  { key: "spread", title: "Prices disagreeing more than usual", rule: "How far this month's trade quotes sit from their reference's median, against the model's normal. Wider means bargains, or a move under way." },
];

const fetchSignals = async (storeIdM) => {
  const { data } = await axios.get("/api/core-list/arbitrage/signals", { params: { store_id_m: storeIdM } });
  return data;
};

const Mini = ({ values }) => {
  if (!values?.length || values.length < 2) return <span className="ms-mini" />;
  const lo = Math.min(...values);
  const span = Math.max(...values) - lo || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * 60 + 2},${16 - ((v - lo) / span) * 14}`).join(" ");
  return (
    <svg className="ms-mini" viewBox="0 0 64 18" aria-hidden="true">
      <polyline points={points} fill="none" />
    </svg>
  );
};

const Signal = ({ meta, data, onShow }) => {
  const [tab, setTab] = useState(meta.tabs?.find((t) => data.tabs?.[t]?.length) || meta.tabs?.[0]);
  const [showRule, setShowRule] = useState(false);
  const rows = meta.tabs ? data.tabs?.[tab] || [] : data.rows || [];

  return (
    <section className="ms-signal">
      <header className="ms-signal__head">
        <h3>{meta.title}</h3>
        <button
          type="button"
          className="ms-info"
          aria-expanded={showRule}
          aria-label={`How "${meta.title}" works`}
          onClick={() => setShowRule((v) => !v)}
        >
          <Icon name="Info" size="sm" />
        </button>
      </header>
      {showRule && <p className="ms-signal__rule">{meta.rule}</p>}

      {meta.tabs && (
        <div className="ms-tabs" role="group" aria-label={`${meta.title} view`}>
          {meta.tabs.map((t) => (
            <button key={t} type="button" aria-pressed={t === tab} onClick={() => setTab(t)}>
              {t}
              {data.tabs?.[t]?.length ? <span>{data.tabs[t].length}</span> : null}
            </button>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="ms-none">Nothing here tonight.</p>
      ) : (
        <ul className="ms-rows">
          {rows.map((row) => (
            <li key={row.model_id}>
              <details className="ms-row">
                <summary>
                  <span className="ms-row__model">{row.label}</span>
                  <Mini values={row.series} />
                  <span className="ms-row__value">
                    <b>{row.main}</b>
                    <small>{row.sub}</small>
                  </span>
                </summary>
                <ul className="ms-refs">
                  {row.refs.map((ref) => <li key={ref}>{ref}</li>)}
                </ul>
                <button type="button" className="ms-show" onClick={() => onShow([row.model_id], row.label)}>
                  Show on the board
                </button>
              </details>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 1 && (
        <button type="button" className="ms-show ms-show--all"
          onClick={() => onShow(rows.map((r) => r.model_id), `${meta.title} · ${rows.length} models`)}>
          Show all {rows.length} models
        </button>
      )}
    </section>
  );
};

const MarketSignalsPanel = ({ open, onClose, storeIdM, onShowModels }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["arbitrage-signals", storeIdM],
    queryFn: () => fetchSignals(storeIdM),
    enabled: storeIdM != null,
    staleTime: 5 * 60_000,
  });

  const signals = data?.signals || {};
  const present = SIGNALS.filter((s) => signals[s.key]);

  return (
    <Modal open={open} onClose={onClose} variant="drawer" size="lg" title="Market signals" className="ms-panel">
      {isLoading ? (
        <StatePanel state="loading" compact title="Loading signals…" />
      ) : isError ? (
        <StatePanel state="error" compact title="Couldn't load signals" />
      ) : present.length === 0 ? (
        <StatePanel
          state="empty"
          icon="Globe"
          title="No signals tonight"
          description="Signals appear when the data shows them. Route lifecycle needs a week of history."
        />
      ) : (
        <div className="ms-list">
          {present.map((meta) => (
            <Signal
              key={meta.key}
              meta={meta}
              data={signals[meta.key]}
              onShow={(ids, label) => {
                onShowModels(ids, label);
                onClose();
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default MarketSignalsPanel;
